import _ from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchHolterEventIndex,
  fetchHolterEvents,
  applyGroupEventChange,
  calculateStartStopStripMomentOfListEvent,
  calculateStartStopStripMomentOfListEventV2,
  formatEventStrip,
  generateFilterSpecialEventType,
  handleAddEventOthersV2,
  isAppliedFilterEvent,
  updateEventChangeAndNew,
} from '@/entities/event';
import {
  selectAiChannel,
  selectChartHrsData,
  selectControlModal,
  selectEcgObject,
  selectEventFilter,
  selectHolterEvents,
  selectIsActiveTab,
  selectIsLoadingHrChart,
  selectIsLoadingHrChartOptions,
  selectIsViewNewEvent,
  selectJumpPortion,
  selectJumpTime,
  selectMinuteInfoEvents,
  selectBeatChanges,
  selectEventChanges,
  selectEventNews,
  selectEventOthers,
  selectNextColumnInfo,
  selectPickDateAction,
  selectPickedEndDate,
  selectPickedStartDate,
  selectPrevColumnInfo,
  selectPreventReloadEcg,
  selectReloadEcgViewer,
  selectSelectedColumnInfo,
  selectSelectedEvent,
  selectSelectedStrip,
  selectGroupEventChanges,
  useReportStore,
} from '@/entities/report';
import { CAPTURE_MODE, ECG_VIEWER_CONFIG_STORE } from '@/shared/ui';
import {
  EMITTER_CONSTANTS,
  LIST_EVENT_TYPE_QUERY,
  KEY_RECOIL,
  STRIP_EVENT_THUMBNAIL_INFO,
  TYPE_BEAT_ENUM,
  TYPE_BEAT_EVENT_ENUM,
  TYPE_EVENT_OTHER,
  TYPE_RHYTHM_EVENT_ENUM,
  canAvgRhythmEvents,
  canShowCountBeatEvents,
  canShowDurationEvents,
  typesPersistEvent,
} from '@/shared/config';
import { convertToMomentJs, eventTypeConvert } from '@/shared/lib/aiUtils';
import { useDeepCompareEffect, useEmitter, useUpdateEffect } from '@/shared/lib/customHooks';
import { toastrError, toastrSuccess } from '@/shared/lib/toastNotification';
import { TPickDateAction } from '@/shared/model';

import { formatDrawDisplay, formatDrawMutation, checkAndProcessEcgData } from '@/entities/ecg';
import {
  checkAndProcessBeatData,
  countBeatChannels,
  generateUpdatedBeats,
  getUpdatedBeatMarker,
  handleUpdateBeatChanges,
  updateListImagesChangeV2,
} from '@/entities/beat';
import { logError } from '@/shared/lib/apiHelpers';
import { isRangesOverlap } from '@/shared/lib/timeRange';
import type { EcgViewerWrapperProps } from './ecgViewerWrapper';

/**
 * Drives the ECG viewer of the ECG events tab, loading the strip for the selected minute and keeping its overlays, beat markers and capture state in sync. Wires the tab-keyed report-store reads and setters, downloads ECG, beat and Holter event data for the selected column, rebuilds the viewer object on beat, bookmark, filter and config changes, and exposes capture, beat-edit, date-pick and jump-to-time handlers.
 *
 * @param props - Viewer inputs carrying the study and profile ids, the ECG data map, the active event type, the timezone offset and the arrow-button callback.
 * @returns An object carrying the loading flags, the ECG object, the strip and chart refs, the capture, beat-update and arrow handlers, the pick-date state, the event and bookmark indicators, the AI channel and the selected event.
 */
const useEcgViewerWrapper = ({
  ecgDataMap,
  studyId,
  profileId,
  timezoneOffset,
  activeButton,
  handleClickArrowButton,
}: Pick<
  EcgViewerWrapperProps,
  | 'ecgDataMap'
  | 'studyId'
  | 'profileId'
  | 'timezoneOffset'
  | 'activeButton'
  | 'handleClickArrowButton'
>) => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;

  const isLoadingHrChart = useReportStore(selectIsLoadingHrChart(keyRecoil));
  const isLoadingHrChartOptions = useReportStore(selectIsLoadingHrChartOptions(keyRecoil));
  /**
   * Reads the accumulated edits to existing ECG events for this tab out of the report store. Applies the tab-keyed event-changes selector to the current state and casts the result to a loose array because the stored type is narrower than the callers need.
   *
   * @returns The edited events for this tab.
   */
  const getEventChanges = (() =>
    selectEventChanges(keyRecoil)(useReportStore.getState())) as unknown as () => any[];
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Stores the tab's edits to existing ECG events. Forwards the value to the event-changes action together with this tab's key.
   *
   * @param v - The edited events or an updater receiving the previous ones.
   * @returns Nothing.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));
  /**
   * Reads the events created during this session for the ECG events tab out of the report store. Applies the tab-keyed new-events selector to the current state instead of subscribing to it.
   *
   * @returns The newly created events for this tab.
   */
  const getEventNews = () => selectEventNews(keyRecoil)(useReportStore.getState());
  const setEventNewsAction = useReportStore((s) => s.actions.setEventNews);
  /**
   * Stores the events created during this session for the tab. Forwards the value to the new-events action together with this tab's key.
   *
   * @param v - The new events or an updater receiving the previous ones.
   * @returns Nothing.
   */
  const setEventNews = (v: Parameters<typeof setEventNewsAction>[1]) =>
    setEventNewsAction(keyRecoil, v);
  const eventNews = useReportStore(selectEventNews(keyRecoil));
  /**
   * Reads the pending beat edits of the ECG events tab out of the report store. Applies the tab-keyed beat-changes selector to the current state instead of subscribing to it.
   *
   * @returns The pending beat changes for this tab.
   */
  const getBeatChanges = () => selectBeatChanges(keyRecoil)(useReportStore.getState());
  const setBeatChangesAction = useReportStore((s) => s.actions.setBeatChanges);
  /**
   * Stores the tab's pending beat edits in the report store. Forwards the value to the beat-changes action together with this tab's key.
   *
   * @param v - The beat changes or an updater receiving the previous ones.
   * @returns Nothing.
   */
  const setBeatChanges = (v: Parameters<typeof setBeatChangesAction>[1]) =>
    setBeatChangesAction(keyRecoil, v);
  const setIsLoadingEcgViewerAction = useReportStore((s) => s.actions.setIsLoadingEcgViewer);
  /**
   * Marks the ECG viewer of the tab as loading or idle. Forwards the value to the loading action together with this tab's key.
   *
   * @param v - The loading flag or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsLoadingEcgViewer = (v: Parameters<typeof setIsLoadingEcgViewerAction>[1]) =>
    setIsLoadingEcgViewerAction(keyRecoil, v);
  const pickDateAction = useReportStore(selectPickDateAction(keyRecoil));
  const setPickDateActionAction = useReportStore((s) => s.actions.setPickDateAction);
  /**
   * Stores which event boundary the technician is picking, or clears the pick. Forwards the value to the pick-date action together with this tab's key.
   *
   * @param v - The pick-date action or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPickDateAction = (v: Parameters<typeof setPickDateActionAction>[1]) =>
    setPickDateActionAction(keyRecoil, v);
  const pickedStartDate = useReportStore(selectPickedStartDate(keyRecoil));
  const setPickedStartDateAction = useReportStore((s) => s.actions.setPickedStartDate);
  /**
   * Stores the start timestamp picked inside the ECG strip. Forwards the value to the picked-start-date action together with this tab's key.
   *
   * @param v - The ISO start timestamp or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPickedStartDate = (v: Parameters<typeof setPickedStartDateAction>[1]) =>
    setPickedStartDateAction(keyRecoil, v);
  const pickedEndDate = useReportStore(selectPickedEndDate(keyRecoil));
  const setPickedEndDateAction = useReportStore((s) => s.actions.setPickedEndDate);
  /**
   * Stores the end timestamp picked inside the ECG strip. Forwards the value to the picked-end-date action together with this tab's key.
   *
   * @param v - The ISO end timestamp or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPickedEndDate = (v: Parameters<typeof setPickedEndDateAction>[1]) =>
    setPickedEndDateAction(keyRecoil, v);
  const selectedColumnInfo = useReportStore(selectSelectedColumnInfo(keyRecoil));
  const prevColumnInfo = useReportStore(selectPrevColumnInfo(keyRecoil));
  const nextColumnInfo = useReportStore(selectNextColumnInfo(keyRecoil));
  const aiChannel = useReportStore(selectAiChannel(keyRecoil));
  const setAiChannelAction = useReportStore((s) => s.actions.setAiChannel);
  /**
   * Stores the AI channel derived from the beat channels of the loaded strip. Forwards the value to the report store action together with this tab's key.
   *
   * @param v - The AI channel value or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setAiChannel = (v: Parameters<typeof setAiChannelAction>[1]) =>
    setAiChannelAction(keyRecoil, v);
  const jumpTime = useReportStore(selectJumpTime(keyRecoil));
  const setJumpTimeAction = useReportStore((s) => s.actions.setJumpTime);
  /**
   * Stores the epoch the viewer must jump to, or clears it once the jump ran. Forwards the value to the jump-time action together with this tab's key.
   *
   * @param v - The jump epoch, undefined to clear it, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpTime = (v: Parameters<typeof setJumpTimeAction>[1]) =>
    setJumpTimeAction(keyRecoil, v);
  const ecgObject = useReportStore(selectEcgObject(keyRecoil));
  const setEcgObjectAction = useReportStore((s) => s.actions.setEcgObject);
  /**
   * Publishes the rebuilt ECG viewer object for the tab. Forwards the value to the store action together with this tab's key.
   *
   * @param v - The ECG viewer object or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setEcgObject = (v: Parameters<typeof setEcgObjectAction>[1]) =>
    setEcgObjectAction(keyRecoil, v);
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));
  const reloadEcgViewer = useReportStore(selectReloadEcgViewer(keyRecoil));
  /**
   * Reads the heart-rate chart series of the ECG events tab out of the report store. Applies the tab-keyed chart HR selector to the current state instead of subscribing to it.
   *
   * @returns The heart-rate chart data points for this tab.
   */
  const getChartHrsData = () => selectChartHrsData(keyRecoil)(useReportStore.getState());
  /**
   * Reads the other-type captured strips of the ECG events tab out of the report store. Applies the tab-keyed selector to the current state and casts the result to a loose array for callers that filter and flatten it.
   *
   * @returns The other-type event strips for this tab.
   */
  const getEventOthers = (() =>
    selectEventOthers(keyRecoil)(useReportStore.getState())) as unknown as () => any[];
  const eventOthers = useReportStore(selectEventOthers(keyRecoil));
  const setEventOthersAction = useReportStore((s) => s.actions.setEventOthers);
  /**
   * Stores the other-type captured strips for the tab. Forwards the value to the other-events action together with this tab's key.
   *
   * @param v - The other-type events or an updater receiving the previous ones.
   * @returns Nothing.
   */
  const setEventOthers = (v: Parameters<typeof setEventOthersAction>[1]) =>
    setEventOthersAction(keyRecoil, v);
  /**
   * Reads the window portion applied when the viewer jumps to a time. Applies the tab-keyed jump-portion selector to the current report store state.
   *
   * @returns The portion used to position the target sample inside the window.
   */
  const getJumpPortion = () => selectJumpPortion(keyRecoil)(useReportStore.getState());
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Stores the window portion applied on the next jump of the viewer. Forwards the value to the jump-portion action together with this tab's key.
   *
   * @param v - The portion value or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  const ecgBookmarks = useReportStore((s) => s.ecgBookmarks);
  const ecgBookmarkDeleteIds = useReportStore((s) => s.ecgBookmarkDeleteIds);
  /**
   * Reads the Holter events currently paged into the ECG events tab out of the report store. Applies the tab-keyed selector to the current state and casts to a loose array because callers read fields absent from the stored type.
   *
   * @returns The Holter events on the current page.
   */
  const getHolterEvents = (() =>
    selectHolterEvents(keyRecoil)(useReportStore.getState())) as unknown as () => any[];
  /**
   * Reads the study's special event type configuration used to build event queries. Takes the value straight off the current report store state, unkeyed by tab.
   *
   * @returns The special event types configured for the study.
   */
  const getSpecialEventTypes = () => useReportStore.getState().specialEventTypes;
  /**
   * Reads the active event filter of the ECG events tab out of the report store. Applies the tab-keyed event-filter selector to the current state so the value is fresh at call time.
   *
   * @returns The event filter settings for this tab.
   */
  const getEventFilter = () => selectEventFilter(keyRecoil)(useReportStore.getState());
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Stores the strip list page index so the viewer can page to a target event. Forwards the value to the page-index action together with this tab's key.
   *
   * @param v - The page index or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  /**
   * Reads the modal control flags of the ECG events tab out of the report store. Applies the tab-keyed control-modal selector to the current state so callers see the latest flags without re-rendering.
   *
   * @returns The control modal flags for this tab.
   */
  const getControlModal = () => selectControlModal(keyRecoil)(useReportStore.getState());
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Stores the strip selected in the tab, or clears the selection. Forwards the value to the selected-strip action together with this tab's key.
   *
   * @param v - The selected strip, null to clear it, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  /**
   * Reads the strip currently selected in the ECG events tab out of the report store. Applies the tab-keyed selector to the current state and casts to a loose type because the stored union spans events, beats and ECG bookmarks.
   *
   * @returns The currently selected strip.
   */
  const getSelectedStrip = (() =>
    selectSelectedStrip(keyRecoil)(useReportStore.getState())) as unknown as () => any;
  /**
   * Reads the pending grouped event changes of the ECG events tab out of the report store. Applies the tab-keyed group-changes selector to the current state so freshly downloaded events can be reconciled against it.
   *
   * @returns The grouped event changes for this tab.
   */
  const getGroupEventChanges = () => selectGroupEventChanges(keyRecoil)(useReportStore.getState());
  const groupEventChanges = useReportStore(selectGroupEventChanges(keyRecoil));
  const setSelectedEventInfoAction = useReportStore((s) => s.actions.setSelectedEventInfo);
  /**
   * Stores the deduplicated events overlapping the loaded strip window. Forwards the value to the selected-event-info action together with this tab's key.
   *
   * @param v - The selected event info or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedEventInfo = (v: Parameters<typeof setSelectedEventInfoAction>[1]) =>
    setSelectedEventInfoAction(keyRecoil, v);
  const selectedEvent = useReportStore(selectSelectedEvent(keyRecoil)) as any;
  /**
   * Reads the event currently selected in the ECG events tab out of the report store. Applies the tab-keyed selector to the current state and casts to a loose type because the stored union omits fields the callers read.
   *
   * @returns The currently selected event.
   */
  const getSelectedEvent = (() =>
    selectSelectedEvent(keyRecoil)(useReportStore.getState())) as unknown as () => any;
  const setMinuteInfoEventsAction = useReportStore((s) => s.actions.setMinuteInfoEvents);
  /**
   * Stores the other-type events belonging to the selected minute. Forwards the value to the minute-info action together with this tab's key.
   *
   * @param v - The minute-info events or an updater receiving the previous ones.
   * @returns Nothing.
   */
  const setMinuteInfoEvents = (v: Parameters<typeof setMinuteInfoEventsAction>[1]) =>
    setMinuteInfoEventsAction(keyRecoil, v);
  /**
   * Reads the other-type events attached to the selected minute. Applies the tab-keyed minute-info selector to the current report store state.
   *
   * @returns The other-type events of the selected minute.
   */
  const getMinuteInfoEvents = () => selectMinuteInfoEvents(keyRecoil)(useReportStore.getState());
  const setIsViewNewEventAction = useReportStore((s) => s.actions.setIsViewNewEvent);
  /**
   * Marks whether the tab is showing a freshly created event. Forwards the boolean to the report store action together with this tab's key.
   *
   * @param v - True while a newly added event is on screen, false once it has been consumed.
   * @returns Nothing.
   */
  const setIsViewNewEvent = (v: boolean) => setIsViewNewEventAction(keyRecoil, v);
  /**
   * Reads whether the tab is currently showing a freshly created event. Applies the tab-keyed flag selector to the current report store state.
   *
   * @returns True while a newly added event is being viewed.
   */
  const getIsViewNewEvent = () => selectIsViewNewEvent(keyRecoil)(useReportStore.getState());
  /**
   * Reads the guard flag that suppresses the next ECG viewer reload. Applies the tab-keyed selector to the current report store state so the guard is visible even mid-download.
   *
   * @returns True when the next reload must be skipped.
   */
  const getPreventReloadEcg = () => selectPreventReloadEcg(keyRecoil)(useReportStore.getState());
  const setPreventReloadEcgAction = useReportStore((s) => s.actions.setPreventReloadEcg);
  /**
   * Raises or clears the guard that suppresses the next ECG viewer reload. Forwards the value to the store action together with this tab's key.
   *
   * @param v - The guard flag or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPreventReloadEcg = (v: Parameters<typeof setPreventReloadEcgAction>[1]) =>
    setPreventReloadEcgAction(keyRecoil, v);
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Stores the event options snapshot that undo and redo replay. Forwards the value to the event-options action together with this tab's key.
   *
   * @param v - The event options or an updater receiving the previous ones.
   * @returns Nothing.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  const beatChanges = useReportStore(selectBeatChanges(keyRecoil));
  const isRenderViewerEcg = useReportStore((s) => s.isRenderViewerEcg);
  const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
  /**
   * Stores the beat options snapshot that undo and redo replay. Forwards the value to the beat-options action together with this tab's key.
   *
   * @param v - The beat options or an updater receiving the previous ones.
   * @returns Nothing.
   */
  const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
    setBeatOptionsAction(keyRecoil, v);
  const eventFilter = useReportStore(selectEventFilter(keyRecoil));

  const [isLoading, setIsLoading] = useState(false);
  const [isDisablePrevBtn, setIsDisablePrevBtn] = useState(false);
  const [isDisableNextBtn, setIsDisableNextBtn] = useState(false);

  const ecgObjectRef = useRef({});
  const ecgChartRef = useRef<any>();
  const startStripEpoch = useRef(null);
  const stopStripEpoch = useRef(null);
  const originalBeatMarkers = useRef([]);
  const originalHesBeatStatus = useRef([]);
  const beatMarkersInEcgViewer = useRef([]);
  const hesBeatStatusInEcgViewer = useRef([]);
  const isJumpToEnd = useRef(false);
  const isJumpToStart = useRef(false);
  const currentQueryEventHour = useRef(null);
  const preventReloadEcgViewer = useRef(false);
  const keepEcgPosition = useRef(false);
  const currentSelectedEventId = useRef(null);
  const savedEvents = useRef([]);
  const lastMinuteInfo = useRef(null);
  const lastSelectedColumnInfoRef = useRef<any>();
  const currentMinute = useRef<any>();
  const currentBeatsChangeRef = useRef<any>();
  const currentEcgViewerConfig = useRef({ ...ECG_VIEWER_CONFIG_STORE });

  //* prevent api race condition by not setState data of old selected strip or event
  let isActiveQuery: boolean;
  const isPickingDate =
    pickDateAction === TPickDateAction.START || pickDateAction === TPickDateAction.END;

  /**
   * Converts an ECG event or minute-info record into the overlay descriptor the ECG viewer draws over the strip. Derives start and stop sample positions from the event epochs clamped to the strip window and scaled by the sampling frequency, colours artifact grey, AFib orange and anything else red, labels only red and other-type events, and flags the entry highlighted when it matches the selected strip.
   *
   * @param event - The event or minute-info record to describe; records without start and stop are treated as minute info.
   * @param isSelected - Whether this entry is the one selected in the current event list.
   * @param selectedStrip - The strip whose idEvent decides which overlay is highlighted.
   * @returns An overlay descriptor carrying type, startPos, stopPos, color, id, isSelected, portion and isHighLight.
   */
  const getEventInfoInEcgViewer = ({ event, isSelected = false, selectedStrip }: any) => {
    const { samplingFrequency } = ecgDataMap;
    const isEvent = event.start && event.stop; // not minute info (minute info dont have start and stop)
    let startPos = 0;
    let stopPos = 0;
    let color;
    if (isEvent) {
      const isArtifactEvent = event.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT;
      const isAfibEvent = event.type === TYPE_RHYTHM_EVENT_ENUM.AFIB;
      color = isArtifactEvent ? 'gray' : isAfibEvent ? 'orange' : 'red';
      const startEpoch = new Date(event.start).getTime();
      const stopEpoch = new Date(event.stop).getTime();
      if (startStripEpoch.current <= startEpoch) {
        const eventDiffSeconds = Math.abs(startEpoch - startStripEpoch.current) / 1000;
        startPos = Math.round(eventDiffSeconds * samplingFrequency);
      }
      if (stopEpoch >= stopStripEpoch.current) {
        stopPos = (stopStripEpoch.current - startStripEpoch.current) / 1000;
      } else {
        stopPos = (stopEpoch - startStripEpoch.current) / 1000;
      }
      stopPos = Math.round(stopPos * samplingFrequency);
    }
    return {
      type:
        isEvent && event.type === TYPE_EVENT_OTHER
          ? event.title
          : isEvent && color === 'red'
            ? (eventTypeConvert as any)[event.type]
            : '', // Show event type text in ecg viewer for others event
      startPos,
      stopPos,
      color,
      id: event.id,
      isSelected,
      portion: (TYPE_BEAT_EVENT_ENUM as any)[event.type] ? 2 : 10, // Determine start window when jump to
      isHighLight: selectedStrip?.idEvent === event.idEvent,
    };
  };

  const createObjectEcg = ({
    ecg,
    beats,
    hesBeatStatus,
    events,
    eventIndex,
    mainEvent,
    exportEcgData,
  }: any) => {
    const { channels, gain, samplingFrequency } = ecgDataMap;
    // *: Add capture sample mark
    const selectedEv = getSelectedEvent();
    /**
     * Resolves which event of the loaded minute is the one currently selected as a strip. Matches by idEvent against the selected event read from the report store, requiring that idEvent to be truthy, and falls back to the minute's main event.
     *
     * @returns The matching event from the loaded list, or the main event when nothing matches.
     */
    const findEvent =
      _.find(events, (x) => x.idEvent === selectedEv?.idEvent && selectedEv?.idEvent) || mainEvent;
    currentSelectedEventId.current = findEvent?.idEvent;
    let drawList = [];
    if (findEvent?.type) {
      drawList = findEvent?.draw;
    } else {
      const otherEvents = getEventOthers();
      const filterRemoveOther = _.filter(otherEvents, (x) => !x.isRemovedStrip);
      drawList =
        _.filter(_.flatten(_.map(filterRemoveOther, (event) => event.draw)), (x) => !!x) || [];
    }
    let drawStartSample = 1;
    const eventsInfo = _.map(events, (event, index) =>
      getEventInfoInEcgViewer({
        event,
        isSelected: index === eventIndex,
        selectedStrip: findEvent,
      }),
    );
    const startSampleEpoch = moment(findEvent?.start).valueOf();
    if (
      startSampleEpoch >= startStripEpoch.current &&
      startSampleEpoch <= stopStripEpoch.current &&
      findEvent?.type
    ) {
      const additionalSample = (TYPE_BEAT_EVENT_ENUM as any)[findEvent?.type]
        ? 5 * samplingFrequency
        : samplingFrequency;
      const startSample =
        Math.round(((startSampleEpoch - startStripEpoch.current) / 1000) * samplingFrequency) -
        additionalSample;
      drawStartSample = startSample > 1 ? startSample : 1;
    }
    const { draw } = formatDrawDisplay(
      drawList,
      startStripEpoch.current,
      samplingFrequency,
      stopStripEpoch.current,
    );
    const viewerObject = {
      options: {},
      widgets: draw,
    };
    const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeats(
      beats,
      hesBeatStatus,
      getBeatChanges(),
      startStripEpoch.current,
      stopStripEpoch.current,
    );
    beatMarkersInEcgViewer.current = updatedBeats;
    hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;
    const endStripPos =
      ((stopStripEpoch.current - startStripEpoch.current) / 1000) * samplingFrequency;
    savedEvents.current = _.cloneDeep(events);
    const newEcgObject = {
      dataSamples: ecg,
      adcGain: gain,
      adcZero: 0,
      numChannels: channels.length,
      viewerObject,
      diagnosticLead: 2,
      samplingRate: samplingFrequency,
      events: eventsInfo,
      aiPredict: [
        {
          beats: updatedBeats,
          hesBeatStatus: updatedHesBeatStatus,
          channel: 1,
          hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
        },
      ],
      isAppliedAIByDefault: true,
      isShowVES: true,
      isShowSVES: true,
      isShowBeatMarkers: true,
      jumpOffset: keepEcgPosition.current
        ? ecgChartRef.current?.getOffsetX()
        : isJumpToStart.current
          ? 1
          : isJumpToEnd.current
            ? endStripPos
            : drawStartSample,
      hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
      hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
      notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
      speed: ECG_VIEWER_CONFIG_STORE.spdValue,
      amp: ECG_VIEWER_CONFIG_STORE.ampValue,
      wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
      exportEcgData,
    };
    setEcgObject(newEcgObject);
    keepEcgPosition.current = false;
    ecgObjectRef.current = newEcgObject;
  };

  /**
   * Loads the ECG samples, beats and Holter events for the minute described by the selected HR-chart column and feeds them to the viewer builder. Bails out on empty input or a raised reload guard, fetches the hour of events, merges pending event, new and group changes plus overlapping neighbour-column events, resolves the main event and pages to it, cuts the data down to the computed strip window, then builds the ECG object and updates the prev and next button state.
   *
   * @param selectedColumnInfo - The events and minute info of the HR-chart column the technician selected, with the minute info last.
   * @returns A promise resolving once the viewer object and the loading flags for the selected minute have been updated.
   */
  const downloadDataEcgV2 = async (selectedColumnInfo: any[]) => {
    if (_.isEmpty(selectedColumnInfo)) {
      return;
    }
    if (preventReloadEcgViewer.current) {
      preventReloadEcgViewer.current = false;
      return;
    }

    if (getPreventReloadEcg()) {
      setPreventReloadEcg(false);
      return;
    }
    lastSelectedColumnInfoRef.current = selectedColumnInfo;
    if (
      currentMinute.current !== selectedColumnInfo[selectedColumnInfo.length - 1].startTimeEpoch
    ) {
      setIsLoading(true);
      currentMinute.current = selectedColumnInfo[selectedColumnInfo.length - 1].startTimeEpoch;
    }
    setIsLoadingEcgViewer(true);
    try {
      const type = activeButton;
      const events = [...selectedColumnInfo];
      const minuteInfo = events[events.length - 1];
      const startHours = moment(minuteInfo.startTimeEpoch).startOf('hours');
      const { defaultStartStrip, defaultStopStrip } = calculateStartStopStripMomentOfListEventV2({
        startTimeEpoch: minuteInfo.startTimeEpoch,
      });
      const startSearchDate = startHours.toISOString();
      const stopSearchDate = startHours.clone().add(1, 'hours').toISOString();
      currentQueryEventHour.current = startSearchDate;
      const { events: downloadedEvents } = await fetchHolterEvents(
        {
          studyId,
          profileId,
          types: _.uniq([type, ...LIST_EVENT_TYPE_QUERY]),
          startSearchDate,
          stopSearchDate,
        },
        0,
        true,
        null,
        false,
      );

      const promise = [
        checkAndProcessEcgData({
          ecgDataMap,
          dataLength:
            ((defaultStopStrip.valueOf() - defaultStartStrip.valueOf()) / 1000) *
            ecgDataMap.samplingFrequency,
          momentObject: {
            startMoment: defaultStartStrip,
            stopMoment: defaultStopStrip,
          } as any,
        }),
        checkAndProcessBeatData({
          ecgDataMap,
          momentObject: {
            startMoment: defaultStartStrip,
            stopMoment: defaultStopStrip,
          } as any,
        }),
      ];

      const [ecgData, holterBeats]: any[] = await Promise.all(promise);
      const startMinuteString = moment(minuteInfo.startTimeEpoch).toISOString();
      const stopMinuteString = moment(minuteInfo.startTimeEpoch).add(60, 'seconds').toISOString();
      const downloadEventsFormat = formatEventStrip(downloadedEvents);
      const downloadEventsUpdate = updateEventChangeAndNew(
        downloadEventsFormat,
        getEventChanges(),
        getEventNews(),
        startSearchDate,
        stopSearchDate,
        typesPersistEvent,
        true,
      );
      applyGroupEventChange(downloadEventsUpdate, getGroupEventChanges());
      _.forEach(downloadEventsUpdate, (downloadEvent: any) => {
        if (
          isRangesOverlap(
            downloadEvent.start,
            downloadEvent.stop,
            startMinuteString as any,
            stopMinuteString as any,
          )
        ) {
          downloadEvent.startTimeEpoch = minuteInfo.startTimeEpoch;
          const findEvent = _.findIndex(
            events,
            (x) => x.idEvent && x.idEvent === downloadEvent.idEvent,
          );
          if (findEvent > -1) {
            events[findEvent] = _.cloneDeep(downloadEvent);
          } else {
            events.push(_.cloneDeep(downloadEvent));
          }
        }
      });
      const selectedStrip = getSelectedStrip();
      const eventsActiveType = _.filter(events, (x) => x.oldType === type);
      const findEvent = _.find(events, (x) => x.idEvent === selectedStrip?.idEvent);
      /**
       * Picks the event the ECG viewer should anchor on for the minute of Holter data just downloaded. Uses the strip-matched event or the selected strip itself when the minute matches the selected strip's start epoch, otherwise the active-type event at the minute's event start, then the first active-type event, then the minute info.
       *
       * @returns The event chosen to anchor the viewer for this minute.
       */
      const mainEvent =
        minuteInfo.startTimeEpoch === selectedStrip?.startTimeEpoch
          ? findEvent || selectedStrip
          : _.find(eventsActiveType, (x) =>
              minuteInfo?.eventStart ? x.start === minuteInfo.eventStart : x.oldType === type,
            ) ||
            eventsActiveType[0] ||
            minuteInfo;

      const holterEvents = getHolterEvents();
      const foundEventInCurrentPage = _.find(holterEvents, (x) => x.idEvent === mainEvent?.idEvent);
      let jumpToEventIndex;
      const needToJumpToEventWhenClickInHrChart =
        mainEvent.idEvent && !mainEvent.isNew && mainEvent.oldType === type;
      if (needToJumpToEventWhenClickInHrChart && !foundEventInCurrentPage) {
        const additionalFilter = generateFilterSpecialEventType(type, getSpecialEventTypes());
        const eventFilter = getEventFilter();
        if (isAppliedFilterEvent(eventFilter, type)) {
          _.assign(additionalFilter, {
            ...(eventFilter.isHideReviewed && { isReviewed: false }),
            ...(eventFilter.isCapture && { isCaptured: true }),
            ...(eventFilter.sortOrder &&
              (canShowCountBeatEvents.includes(type) ||
                canShowDurationEvents.includes(type) ||
                canAvgRhythmEvents.includes(type)) && {
                sortBy: eventFilter.sortBy || 'countBeats',
                sortOrder: eventFilter.sortOrder,
              }),
          });
        }
        const filterHolterEventIndex = {
          studyId,
          profileId,
          types: [type],
          includedEventId: mainEvent.idEvent,
          ...additionalFilter,
        };
        jumpToEventIndex = await fetchHolterEventIndex(filterHolterEventIndex, true, null, false);
      }
      if (isActiveQuery) {
        const controlModal = getControlModal();
        if (needToJumpToEventWhenClickInHrChart) {
          if (!(controlModal.isShowAddNewEvent || controlModal.isShowChangeDurationButtonType)) {
            if (!_.isNil(jumpToEventIndex) && jumpToEventIndex >= 0) {
              const newPageIndex = Math.floor(
                jumpToEventIndex / STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit,
              );
              setPageIndex((prev) => {
                if (prev.index !== newPageIndex) {
                  return { index: newPageIndex };
                }
                return prev;
              });
            }
            if (lastMinuteInfo.current !== minuteInfo.startTimeEpoch) {
              // must be change minute
              // selectedStripState (Recoil, Task 7) is typed IEvent | IBeat | IEcgBookmark, missing idEvent on some members - atom-type gap.
              setSelectedStrip((prevStrip: any) => {
                if (prevStrip?.id !== mainEvent.id || prevStrip?.idEvent !== mainEvent.idEvent) {
                  return mainEvent;
                }
                return prevStrip;
              });
            }
          }
        } else {
          const isViewNewEvent = getIsViewNewEvent();
          if (isViewNewEvent) {
            setIsViewNewEvent(false);
          } else if (
            !(controlModal.isShowAddNewEvent || controlModal.isShowChangeDurationButtonType)
          ) {
            //* Not found main event in current minute -> unselect strip
            setSelectedStrip(null);
          }
        }
        const { startStrip, stopStrip } = calculateStartStopStripMomentOfListEvent({
          events,
        });
        const startTimeString = startStrip.toISOString();
        const stopTimeString = stopStrip.toISOString();
        _.forEach(nextColumnInfo, (event: any) => {
          if (
            !_.find(events, (x: any) => x.idEvent === event.idEvent) &&
            isRangesOverlap(event.start, event.stop, startTimeString as any, stopTimeString as any)
          ) {
            events.push(event);
          }
        });
        _.forEach(prevColumnInfo, (event: any) => {
          if (
            !_.find(events, (x: any) => x.idEvent === event.idEvent) &&
            isRangesOverlap(event.start, event.stop, startTimeString as any, stopTimeString as any)
          ) {
            events.push(event);
          }
        });
        const selectedEventInfo = [
          { ...mainEvent, startTimeEpoch: minuteInfo.startTimeEpoch },
          ..._.map(events, (event) => {
            if (event?.idEvent !== mainEvent?.idEvent || event.id !== mainEvent.id) {
              return {
                ...event,
                startTimeEpoch: minuteInfo.startTimeEpoch,
              };
            }
            return null;
          }).filter(Boolean),
        ];
        const uniqEvents = _.uniqWith(selectedEventInfo, (a, b) => {
          const aCopy = { id: a?.id, type: a?.type, idEvent: a?.idEvent };
          const bCopy = { id: b?.id, type: b?.type, idEvent: b?.idEvent };
          return _.isEqual(aCopy, bCopy);
        });
        const eventOthers = getEventOthers();
        setSelectedEventInfo(
          _.uniqBy(
            _.filter(uniqEvents, (x) => !x.isNew && x.type !== TYPE_EVENT_OTHER),
            'idEvent',
          ),
        );
        const otherEvents = _.filter(selectedEventInfo, (x) => x.type === TYPE_EVENT_OTHER);
        const minuteInfoEvs = _.uniqBy([...eventOthers, ...otherEvents], 'id');
        setMinuteInfoEvents(minuteInfoEvs);
        _.remove(events, (x) => x.type === TYPE_BEAT_ENUM.DELETED);
        startStripEpoch.current = startStrip.valueOf();
        stopStripEpoch.current = stopStrip.valueOf();
        // * Cut ecgdata and beat in range
        const cuttedMsStart = startStripEpoch.current - defaultStartStrip.valueOf();
        const cuttedMsStop = defaultStopStrip.valueOf() - stopStripEpoch.current;
        const cuttedSampleStart = (cuttedMsStart / 1000) * ecgDataMap.samplingFrequency;
        const cuttedSampleStop = (cuttedMsStop / 1000) * ecgDataMap.samplingFrequency;
        const exportStart = startStripEpoch.current - 10000;
        const exportStop = stopStripEpoch.current + 10000;
        const ecgExport = await checkAndProcessEcgData({
          ecgDataMap,
          dataLength: ((exportStart - exportStop) / 1000) * ecgDataMap.samplingFrequency,
          momentObject: {
            startMoment: moment(exportStart),
            stopMoment: moment(exportStop),
          } as any,
        });
        const cuttedEcgData = _.map(ecgData as any[], (subArr: any) =>
          subArr.slice(cuttedSampleStart, subArr.length - cuttedSampleStop),
        );
        const cuttedBeatSample: any[] = [];
        const cuttedHesBeatStatus: any[] = [];
        const cuttedBeatChannel: any[] = [];
        if (!_.isEmpty(holterBeats?.beats)) {
          _.forEach(holterBeats.beats, (sample, index) => {
            const epochBeat =
              defaultStartStrip.valueOf() + (sample * 1000) / ecgDataMap.samplingFrequency;
            if (epochBeat >= startStripEpoch.current && epochBeat <= stopStripEpoch.current) {
              cuttedHesBeatStatus.push(holterBeats?.hesBeatStatus[index]);
              cuttedBeatChannel.push(holterBeats?.beatChannels[index]);
              cuttedBeatSample.push(
                Math.round(
                  ((epochBeat - startStripEpoch.current) / 1000) * ecgDataMap.samplingFrequency,
                ),
              );
            }
          });
        }
        const cuttedHolterBeats = {
          beats: cuttedBeatSample,
          hesBeatStatus: cuttedHesBeatStatus,
          beatChannels: cuttedBeatChannel,
        };
        // setSelectedEvent(mainEvent);
        setAiChannel(countBeatChannels(cuttedHolterBeats?.beatChannels) as any);
        const eventIndex = _.findIndex(events, (x) => _.isEqual(x, mainEvent));
        const eventsInfo = _.filter(events, (x) => x.type !== TYPE_EVENT_OTHER);
        createObjectEcg({
          ecg: cuttedEcgData,
          beats: cuttedHolterBeats?.beats,
          hesBeatStatus: cuttedHolterBeats?.hesBeatStatus,
          events: _.uniqWith(eventsInfo, (a, b) => {
            const aCopy = { id: a?.id, type: a?.type, idEvent: a?.idEvent };
            const bCopy = { id: b?.id, type: b?.type, idEvent: b?.idEvent };
            return _.isEqual(aCopy, bCopy);
          }),
          eventIndex:
            eventIndex > -1 ? eventIndex : _.findIndex(events, (x) => _.isEqual(x, minuteInfo)),
          mainEvent,
          exportEcgData: {
            ecgData: ecgExport,
            ecg: cuttedEcgData,
            startEpoch: exportStart,
            stopEpoch: exportStop,
          },
        });
        originalBeatMarkers.current = cuttedHolterBeats?.beats;
        originalHesBeatStatus.current = cuttedHolterBeats?.hesBeatStatus;
        const hrsData = getChartHrsData();
        const prevMinute = minuteInfo.startTimeEpoch - 60000;
        const nextMinute = minuteInfo.startTimeEpoch + 60000;
        const prevMinuteHrData = _.find(hrsData, (x) => x.id === prevMinute);
        const nextMinuteHrData = _.find(hrsData, (x) => x.id === nextMinute);
        setIsDisablePrevBtn(!prevMinuteHrData?.avgHr);
        setIsDisableNextBtn(!nextMinuteHrData?.avgHr);
        setIsLoading(false);
        setIsLoadingEcgViewer(false);
      }
      isJumpToEnd.current = false;
      isJumpToStart.current = false;
      lastMinuteInfo.current = minuteInfo.startTimeEpoch;
    } catch (error) {
      if (!_.includes(error.message, 'canceled')) {
        logError('Failed to download ECG data: ', error);
      }
      setIsLoading(false);
      setIsLoadingEcgViewer(false);
    }
  };

  /**
   * Rebuilds the ECG viewer object so it picks up the latest viewer configuration and beat edits. Skips when no object exists yet, regenerates beats and HES statuses from the originals plus pending beat changes, snapshots the current config, and writes a clone carrying the channel, filter, speed, amplitude and window settings back to the store and the ref.
   *
   * @returns Nothing.
   */
  const reCreateObjectEcg = () => {
    if (!_.isEmpty(ecgObjectRef.current)) {
      const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeats(
        originalBeatMarkers.current,
        originalHesBeatStatus.current,
        getBeatChanges(),
        startStripEpoch.current,
        stopStripEpoch.current,
      );
      currentEcgViewerConfig.current = { ...ECG_VIEWER_CONFIG_STORE };
      beatMarkersInEcgViewer.current = updatedBeats;
      hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;
      const clonedEcgObject = _.cloneDeep(ecgObjectRef.current);
      _.assign(clonedEcgObject, {
        hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
        hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
        notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
        speed: ECG_VIEWER_CONFIG_STORE.spdValue,
        amp: ECG_VIEWER_CONFIG_STORE.ampValue,
        wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
        aiPredict: [
          {
            beats: updatedBeats,
            hesBeatStatus: updatedHesBeatStatus,
            channel: 1,
            hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
          },
        ],
      });
      setEcgObject(clonedEcgObject);
      ecgObjectRef.current = clonedEcgObject;
    }
  };

  /**
   * Records the timestamp the technician clicked inside the ECG strip while picking an event boundary. Acts only while a pick is in progress, converting the strip start epoch plus the clicked offset into an ISO string in the study timezone and storing it as the picked start or end date according to the current pick action.
   *
   * @param diffSeconds - Offset in seconds from the strip start to the clicked position.
   * @returns Nothing.
   */
  const handlePickDate = (diffSeconds: number) => {
    if (isPickingDate) {
      const date = convertToMomentJs(startStripEpoch.current, timezoneOffset)
        .add(diffSeconds, 'seconds')
        .toISOString();
      if (pickDateAction === TPickDateAction.START) {
        setPickedStartDate(date);
      } else {
        setPickedEndDate(date);
      }
    }
  };

  /**
   * Refreshes the ECG viewer overlays and drawings after a strip capture or a change of selected event, without reloading any data. Rebuilds the event overlays from the saved events, resolves the draw list from the selected event's pending changes or from the other events, regenerates the beat markers, and assigns the result plus the viewer config onto a clone, jumping to the event start only when a position is requested.
   *
   * @param isGetPosition - Whether to recompute the jump offset from the selected event start instead of keeping the current scroll position.
   * @returns Nothing.
   */
  const updateEcgCapture = (isGetPosition = false) => {
    if (_.isEmpty(ecgObjectRef.current)) return;
    const clonedEcgObject = _.cloneDeep(ecgObjectRef.current);
    const { samplingFrequency } = ecgDataMap;
    const selectedEv = getSelectedEvent();
    const findEvent = _.find(savedEvents.current, (x) => x?.idEvent === selectedEv?.idEvent);
    const eventsInfo = _.map(savedEvents.current, (event) =>
      getEventInfoInEcgViewer({
        event,
        isSelected: findEvent?.idEvent === event?.idEvent,
        selectedStrip: findEvent,
      }),
    );
    let drawStartSample = 1;
    if (isGetPosition) {
      const startSampleEpoch = moment(findEvent?.start).valueOf();
      if (
        startSampleEpoch >= startStripEpoch.current &&
        startSampleEpoch <= stopStripEpoch.current &&
        findEvent?.type
      ) {
        const additionalSample = (TYPE_BEAT_EVENT_ENUM as any)[findEvent?.type]
          ? 5 * samplingFrequency
          : samplingFrequency;
        const startSample =
          Math.round(((startSampleEpoch - startStripEpoch.current) / 1000) * samplingFrequency) -
          additionalSample;
        drawStartSample = startSample > 1 ? startSample : 1;
      }
    }
    const eventChanges = getEventChanges();
    const selectedEvent = getSelectedEvent();
    const selectedEventIndex = _.findIndex(
      eventChanges,
      (x) => x.idEvent === selectedEvent.idEvent,
    );
    const selectedEventChanges =
      selectedEventIndex > -1 ? eventChanges[selectedEventIndex] : selectedEvent;
    let drawList = [];
    if (selectedEventChanges?.type) {
      drawList = selectedEventChanges?.draw;
    } else {
      const otherEvents = getEventOthers();
      const filterRemoveOther = _.filter(otherEvents, (x) => !x.isRemovedStrip);
      drawList =
        _.filter(_.flatten(_.map(filterRemoveOther, (event) => event.draw)), (x) => !!x) || [];
    }
    const { draw } = formatDrawDisplay(
      drawList,
      startStripEpoch.current,
      samplingFrequency,
      stopStripEpoch.current,
    );

    const viewerObject = {
      options: {},
      widgets: draw,
    };

    const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeats(
      originalBeatMarkers.current,
      originalHesBeatStatus.current,
      getBeatChanges(),
      startStripEpoch.current,
      stopStripEpoch.current,
    );
    beatMarkersInEcgViewer.current = updatedBeats;
    hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;

    _.assign(clonedEcgObject, {
      viewerObject,
      events: eventsInfo,
      jumpOffset: isGetPosition ? drawStartSample : ecgChartRef.current?.getOffsetX(),
      hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
      hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
      notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
      speed: ECG_VIEWER_CONFIG_STORE.spdValue,
      amp: ECG_VIEWER_CONFIG_STORE.ampValue,
      wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
      aiPredict: [
        {
          beats: updatedBeats,
          hesBeatStatus: updatedHesBeatStatus,
          channel: 1,
          hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
        },
      ],
    });
    setEcgObject(clonedEcgObject);
    ecgObjectRef.current = clonedEcgObject;
  };
  useEffect(() => {
    setTimeout(() => {
      if (selectedEvent.idEvent && currentSelectedEventId.current !== selectedEvent.idEvent) {
        currentSelectedEventId.current = selectedEvent.idEvent;
        updateEcgCapture(true);
      }
    }, 1);
  }, [selectedEvent.idEvent]);
  useEmitter(EMITTER_CONSTANTS.UPDATE_CAPTURE_EVENT, updateEcgCapture, [ecgObject]);

  const handleCaptureImages = ({
    draw,
    allIndexStripSvg,
    technicianComments,
    captureMode,
    captureTitle,
    visibleChannels,
  }: any) => {
    try {
      if (allIndexStripSvg.length > 0) {
        if (captureMode === CAPTURE_MODE.EVENT) {
          if (selectedEvent.type === TYPE_RHYTHM_EVENT_ENUM.AFIB) {
            keepEcgPosition.current = true;
          } else {
            preventReloadEcgViewer.current = true;
          }
          const { samplingFrequency } = ecgDataMap;
          const { formatDraw } = formatDrawMutation(
            draw,
            samplingFrequency,
            startStripEpoch.current,
            timezoneOffset,
            visibleChannels,
          );
          if (selectedEvent.isNew) {
            const data = updateListImagesChangeV2(
              getEventNews(),
              selectedEvent,
              formatDraw,
              allIndexStripSvg,
              technicianComments,
            );
            // Perform grouping event action applicable to undo/redo
            setEventOptions((prevOptions: any) => ({
              ...prevOptions,
              eventNewsState: data,
            }));
            setEventNews(data);
          } else {
            const data = updateListImagesChangeV2(
              getEventChanges(),
              selectedEvent,
              formatDraw,
              allIndexStripSvg,
              technicianComments,
            );
            // Perform grouping event action applicable to undo/redo
            setEventOptions((prevOptions: any) => ({
              ...prevOptions,
              eventChangesState: data,
            }));
            setEventChanges(data);
          }
          toastrSuccess(
            'You can find captured strip in "Strips Management" tab.',
            'Strip captured',
          );
          updateEcgCapture();
        }
        if (captureMode === CAPTURE_MODE.OTHER) {
          const { samplingFrequency } = ecgDataMap;
          const { formatDraw, stripStart, stripStop } = formatDrawMutation(
            draw,
            samplingFrequency,
            startStripEpoch.current,
            timezoneOffset,
            visibleChannels,
          );
          const startTime = stripStart;
          const endTime = stripStop;
          const data = handleAddEventOthersV2({
            events: getEventOthers(),
            draw: formatDraw,
            allIndexStripSvg,
            technicianComments,
            captureTitle,
            startTime,
            endTime,
            miniteInfo: getMinuteInfoEvents(),
          } as any);
          // Perform grouping event action applicable to undo/redo
          setEventOptions((prevOptions: any) => ({
            ...prevOptions,
            eventOthersState: data,
          }));
          setEventOthers(data);
          updateEcgCapture();
        }
      }
    } catch (error) {
      logError('Failed to capture images: ', error);
      toastrError(error.message, 'Error');
    }
  };

  /**
   * Applies beat marker edits made inside the ECG viewer to the tab's pending beat changes. Diffs the incoming markers and HES statuses against the ones currently drawn, or the originals when the viewer holds none, folds the diff into the beat-changes state, mirrors the result into the undo and redo beat options, and caches the new markers on the refs.
   *
   * @param updatedBeatObject - The viewer payload carrying currentBeatMarkers and currentHesBeatStatus after the edit.
   * @returns Nothing.
   */
  const handleUpdateBeat = (updatedBeatObject: any) => {
    const { currentBeatMarkers, currentHesBeatStatus } = updatedBeatObject;

    const updatedBeatMarkers = getUpdatedBeatMarker(
      currentBeatMarkers,
      _.isEmpty(beatMarkersInEcgViewer.current)
        ? originalBeatMarkers.current
        : beatMarkersInEcgViewer.current,
      currentHesBeatStatus,
      _.isEmpty(hesBeatStatusInEcgViewer.current)
        ? originalHesBeatStatus.current
        : hesBeatStatusInEcgViewer.current,
      originalHesBeatStatus.current,
      startStripEpoch.current,
      ecgDataMap.samplingFrequency,
    );
    let beatsChanges: any[] = [];
    setBeatChanges((currBeatChange: any) => {
      const updatedBeatChanges = handleUpdateBeatChanges(updatedBeatMarkers, currBeatChange);
      currentBeatsChangeRef.current = updatedBeatChanges;
      beatsChanges = updatedBeatChanges;
      return updatedBeatChanges;
    });
    setBeatOptions((prev: any) => ({ ...prev, beatChangesState: beatsChanges }));
    beatMarkersInEcgViewer.current = currentBeatMarkers;
    hesBeatStatusInEcgViewer.current = currentHesBeatStatus;
  };

  /**
   * Steps the ECG viewer to the neighbouring minute when the previous or next arrow is pressed. Raises the jump-to-start ref for a forward step and the jump-to-end ref for a backward one, then delegates to the handleClickArrowButton prop, memoised once with an empty dependency list.
   *
   * @param isNext - True to move to the following minute, false to move to the preceding one.
   * @returns Nothing.
   */
  const clickArrowButtonHandle = useCallback((isNext: boolean) => {
    if (isNext) {
      isJumpToStart.current = true;
    } else {
      isJumpToEnd.current = true;
    }
    handleClickArrowButton(isNext);
  }, []);

  /**
   * Decides which capture mode the strip capture flow should use for the currently selected event. Returns the artifact mode for artifact and deleted-beat events, the event mode for any other typed event, and the other mode when nothing is selected.
   *
   * @returns The capture mode constant matching the current selection.
   */
  const getCaptureMode = () => {
    if (selectedEvent?.type) {
      if (
        selectedEvent.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT ||
        selectedEvent.type === TYPE_BEAT_ENUM.DELETED
      ) {
        return CAPTURE_MODE.ARTIFACT;
      }
      return CAPTURE_MODE.EVENT;
    }
    return CAPTURE_MODE.OTHER;
  };

  const selectedColumnId = useMemo(
    () => _.map(selectedColumnInfo, (x: any) => x.idEvent || x.id),
    [selectedColumnInfo],
  );

  useDeepCompareEffect(
    () => {
      isActiveQuery = true;
      if (isRenderViewerEcg) {
        if (keepEcgPosition.current && lastSelectedColumnInfoRef.current) {
          downloadDataEcgV2(lastSelectedColumnInfoRef.current);
        } else {
          downloadDataEcgV2(selectedColumnInfo);
        }
      }
    },
    [
      selectedColumnId,
      groupEventChanges,
      eventNews,
      reloadEcgViewer,
      isRenderViewerEcg,
      eventChanges,
    ],
    () => {
      isActiveQuery = false;
    },
  );

  /**
   * Redraws the ECG viewer in place after bookmarks, other events, filters or beat edits change, keeping the current scroll offset. Regenerates beats from the originals plus pending changes, resolves the draw list from the selected event's edits or from the other events, reformats it for the strip window, assigns the config and jump offset onto a clone, and resets the chart mode when beats changed.
   *
   * @param isChangeBeats - Whether the redraw follows a beat edit, which additionally resets the chart mode.
   * @returns Nothing.
   */
  const reDrawEcgObjectKeepPosition = (isChangeBeats?: boolean) => {
    if (!_.isEmpty(ecgObjectRef.current)) {
      const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeats(
        originalBeatMarkers.current,
        originalHesBeatStatus.current,
        getBeatChanges(),
        startStripEpoch.current,
        stopStripEpoch.current,
      );
      beatMarkersInEcgViewer.current = updatedBeats;
      hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;

      const clonedEcgObject = _.cloneDeep(ecgObjectRef.current);
      const { samplingFrequency } = ecgDataMap;
      const eventChanges = getEventChanges();
      const selectedEvent = getSelectedEvent();
      const selectedEventIndex = _.findIndex(
        eventChanges,
        (x) => x.idEvent === selectedEvent.idEvent,
      );
      const selectedEventChanges =
        selectedEventIndex > -1 ? eventChanges[selectedEventIndex] : selectedEvent;
      const otherEvents = getEventOthers();
      const drawList = selectedEventChanges?.type
        ? selectedEventChanges?.draw
        : _.filter(_.flatten(_.map(otherEvents, (event) => event.draw)), (x) => !!x) || [];
      const { draw } = formatDrawDisplay(
        drawList,
        startStripEpoch.current,
        samplingFrequency,
        stopStripEpoch.current,
      );

      const viewerObject = {
        options: {},
        widgets: draw,
      };

      _.assign(clonedEcgObject, {
        viewerObject,
        jumpOffset: ecgChartRef.current?.getOffsetX() || 1,
        hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
        hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
        notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
        speed: ECG_VIEWER_CONFIG_STORE.spdValue,
        amp: ECG_VIEWER_CONFIG_STORE.ampValue,
        wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
        aiPredict: [
          {
            beats: updatedBeats,
            hesBeatStatus: updatedHesBeatStatus,
            channel: 1,
            hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
          },
        ],
      });
      setEcgObject(clonedEcgObject);
      if (isChangeBeats) {
        if (ecgChartRef.current) {
          ecgChartRef.current.resetInMode(true);
        }
      }
      ecgObjectRef.current = clonedEcgObject;
    }
  };

  // Re-create ECG object when beat changes
  useUpdateEffect(() => {
    if (isRenderViewerEcg) {
      reDrawEcgObjectKeepPosition();
    }
  }, [ecgBookmarks, eventOthers, isRenderViewerEcg]);

  useUpdateEffect(() => {
    if (!_.isEqual(beatChanges, currentBeatsChangeRef.current)) {
      currentBeatsChangeRef.current = beatChanges;
      reDrawEcgObjectKeepPosition(true);
    }
  }, [beatChanges]);

  useUpdateEffect(() => {
    if (isRenderViewerEcg) {
      reDrawEcgObjectKeepPosition();
    }
  }, [eventFilter]);

  useUpdateEffect(() => {
    if (isActiveTab) {
      if (ecgChartRef.current) {
        ecgChartRef.current.resetInMode();
      }
      if (!_.isEqual(currentEcgViewerConfig.current, ECG_VIEWER_CONFIG_STORE)) {
        reCreateObjectEcg();
      }
    } else {
      reCreateObjectEcg();
    }
  }, [isActiveTab]);

  useUpdateEffect(() => {
    /**
     * Scrolls the ECG chart to the requested jump time once the chart has finished loading. Converts the jump epoch into a sample position using the sampling frequency, calls the chart jump with the stored portion and clears both values, or retries itself ten milliseconds later while the chart is still loading.
     *
     * @returns Nothing.
     */
    const handleJumpToEcgViewer = () => {
      if (jumpTime <= stopStripEpoch.current && jumpTime >= startStripEpoch.current) {
        if (ecgChartRef.current?.hasLoadChart()) {
          const jumpPos = Math.round(
            (((jumpTime as any) - startStripEpoch.current) / 1000) * ecgDataMap.samplingFrequency,
          );
          const jumpPortion = getJumpPortion();
          ecgChartRef.current.handleJumpTo(jumpPos, jumpPortion);
          setJumpTime(undefined);
          setJumpPortion(0);
        } else {
          setTimeout(() => {
            handleJumpToEcgViewer();
          }, 10);
        }
      }
    };
    if (
      jumpTime &&
      !_.isEmpty(ecgChartRef.current) &&
      startStripEpoch.current &&
      stopStripEpoch.current &&
      !isLoading
    ) {
      handleJumpToEcgViewer();
    }
  }, [
    jumpTime,
    isLoading,
    ecgObject, // trigger useEffect when data has cached
  ]);

  const eventIndicator = useMemo(() => {
    const { samplingFrequency } = ecgDataMap;
    let startPos = null;
    let endPos = null;
    if (pickedStartDate) {
      const pickedStartEpoch = moment(pickedStartDate).valueOf();
      startPos = Math.round(
        ((pickedStartEpoch - startStripEpoch.current) / 1000) * samplingFrequency,
      );
    }
    if (pickedEndDate) {
      const pickedEndEpoch = moment(pickedEndDate).valueOf();
      endPos = Math.round(((pickedEndEpoch - startStripEpoch.current) / 1000) * samplingFrequency);
    }
    return {
      startPos,
      endPos,
    };
  }, [pickedStartDate, pickedEndDate, ecgObject]);

  const bookmarksInRange = useMemo(() => {
    const result: any[] = [];
    _.forEach(ecgBookmarks, (bookmark: any) => {
      if (
        bookmark.center >= startStripEpoch.current &&
        bookmark.center <= stopStripEpoch.current &&
        !ecgBookmarkDeleteIds.includes(bookmark.id)
      ) {
        result.push(bookmark);
      }
    });
    return result;
  }, [ecgBookmarks, ecgBookmarkDeleteIds, ecgObject]);

  const bookmarkIndicators = useMemo(() => {
    const { samplingFrequency } = ecgDataMap;
    const listBookmarks: any[] = [];
    _.forEach(bookmarksInRange, (bookmark: any) => {
      listBookmarks.push({
        sample: Math.round(
          ((bookmark.center - startStripEpoch.current) / 1000) * samplingFrequency,
        ),
        id: bookmark.center,
      });
    });
    return listBookmarks;
  }, [bookmarksInRange, ecgObject]);

  useEffect(() => {
    /**
     * Cancels an in-progress event boundary pick when Escape is released. Ignores auto-repeat key events and resets the pick action to none.
     *
     * @param event - The keyup event, whose key and repeat fields are inspected.
     * @returns Nothing.
     */
    const handleKeyup = (event: any) => {
      if (event?.repeat) {
        return;
      }
      const { key } = event;
      if (key === 'Escape' && pickDateAction) {
        setPickDateAction(TPickDateAction.NONE);
      }
    };
    if (isPickingDate) {
      window.addEventListener('keyup', handleKeyup);
    }
    return () => {
      window.removeEventListener('keyup', handleKeyup);
    };
  }, [pickDateAction, isPickingDate]);

  return {
    isLoading,
    isLoadingHrChart,
    isLoadingHrChartOptions,
    ecgObject,
    startStripEpoch,
    ecgChartRef,
    handlePickDate,
    pickDateAction,
    eventIndicator,
    bookmarkIndicators,
    getCaptureMode,
    handleCaptureImages,
    clickArrowButtonHandle,
    handleUpdateBeat,
    aiChannel,
    isDisableNextBtn,
    isDisablePrevBtn,
    isPickingDate,
    selectedEvent,
  };
};

export default useEcgViewerWrapper;
